import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowLeft, Building2, CreditCard, Trash2, Star, Loader2, Plus, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
import { API_URL as BACKEND_URL } from '../config/api';

/* ─── Add Bank Form (inside Elements) ─── */
const AddBankForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const peOptions = useMemo(() => ({ paymentMethodOrder: ['us_bank_account'] }), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const { error: err } = await stripe.confirmSetup({ elements, redirect: 'if_required' });
      if (err) { setError(err.message); }
      else { onSuccess(); }
    } catch { setError('Something went wrong'); }
    finally { setIsSubmitting(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement options={peOptions} />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex space-x-3">
        <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={isSubmitting || !stripe} className="flex-1 bg-green-600 hover:bg-green-700 text-white">
          {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
          Save Bank
        </Button>
      </div>
    </form>
  );
};

/* ─── Main Page ─── */
const PaymentMethodsPage = () => {
  const navigate = useNavigate();
  const [methods, setMethods] = useState([]);
  const [defaultPm, setDefaultPm] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [addingBank, setAddingBank] = useState(false);
  const [setupSecret, setSetupSecret] = useState(null);
  const [custSessionSecret, setCustSessionSecret] = useState(null);

  useEffect(() => { fetchMethods(); }, []);

  const fetchMethods = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/payment-methods`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMethods(data.payment_methods || []);
        setDefaultPm(data.default_payment_method);
      }
    } catch { /* silent */ }
    setIsLoading(false);
  };

  const handleStartAddBank = async () => {
    setAddingBank(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/payment-methods/setup-intent`, {
        method: 'POST', credentials: 'include',
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = null; }
      
      if (res.ok && data && data.client_secret) {
        setSetupSecret(data.client_secret);
        setCustSessionSecret(data.customer_session_client_secret || null);
      } else {
        const msg = data?.detail 
          ? (typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail))
          : `Error ${res.status}: ${text.substring(0, 200)}`;
        toast.error(msg);
        setAddingBank(false);
      }
    } catch (e) { 
      toast.error('Connection error: ' + (e.message || 'unknown')); 
      setAddingBank(false); 
    }
  };

  const handleBankAdded = async () => {
    setAddingBank(false);
    setSetupSecret(null);
    toast.success('Bank account saved');

    // Auto-link for payout if user has a connected account
    if (setupSecret) {
      const setupIntentId = setupSecret.split('_secret_')[0];
      try {
        await fetch(`${BACKEND_URL}/api/payment-methods/link-payout`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ setup_intent_id: setupIntentId }),
        });
      } catch (e) { console.error('Auto payout link:', e); }
    }

    fetchMethods();
  };

  const handleSetDefault = async (pmId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/payment-methods/default`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_method_id: pmId }),
      });
      if (res.ok) { toast.success('Default updated'); setDefaultPm(pmId); }
      else { const err = await res.json(); toast.error(typeof err.detail === 'string' ? err.detail : 'Failed'); }
    } catch { toast.error('Connection error'); }
  };

  const handleRemove = async (pmId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/payment-methods/remove`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_method_id: pmId }),
      });
      if (res.ok) { toast.success('Removed'); setMethods(prev => prev.filter(m => m.id !== pmId)); }
      else { const err = await res.json(); toast.error(typeof err.detail === 'string' ? err.detail : 'Failed'); }
    } catch { toast.error('Connection error'); }
  };

  const achMethods = methods.filter(m => m.type === 'bank' || m.type === 'us_bank_account');
  const cardMethods = methods.filter(m => m.type === 'card');
  const appearance = useMemo(() => ({ theme: 'stripe', variables: { colorPrimary: '#16a34a', borderRadius: '8px' } }), []);

  const elementsOptions = useMemo(() => {
    if (!setupSecret) return null;
    const opts = { clientSecret: setupSecret, appearance };
    if (custSessionSecret) opts.customerSessionClientSecret = custSessionSecret;
    return opts;
  }, [setupSecret, custSessionSecret, appearance]);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center space-x-4">
          <Button variant="ghost" size="icon" onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/settings')} data-testid="pm-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Bank Accounts</h1>
            <p className="text-xs text-gray-500 mt-0.5">Receive payouts directly to your bank.</p>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-8 space-y-5">
        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
        ) : (
          <>
            {/* Bank Accounts */}
            <Card data-testid="pm-ach-section">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center">
                    <Building2 className="w-5 h-5 mr-2 text-green-600" />
                    Connected bank account
                  </CardTitle>
                  {!addingBank && (
                    <Button size="sm" variant="outline" onClick={handleStartAddBank} data-testid="pm-add-bank-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add bank
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {/* Add bank - loading */}
                {addingBank && !setupSecret && (
                  <div className="flex flex-col items-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-green-500 mb-2" />
                    <p className="text-sm text-gray-500">Setting up bank connection...</p>
                  </div>
                )}

                {/* Add bank form */}
                {addingBank && setupSecret && elementsOptions && (
                  <div className="mb-4 p-4 border border-green-200 rounded-lg bg-green-50" data-testid="pm-add-bank-form">
                    <p className="text-sm font-medium text-gray-700 mb-3">Connect your bank account</p>
                    <Elements stripe={stripePromise} options={elementsOptions}>
                      <AddBankForm onSuccess={handleBankAdded} onCancel={() => { setAddingBank(false); setSetupSecret(null); }} />
                    </Elements>
                  </div>
                )}

                {achMethods.length === 0 && !addingBank ? (
                  <div className="text-center py-8 px-4" data-testid="pm-no-ach">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-50 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-green-600" />
                    </div>
                    <p className="text-sm font-medium text-gray-900 mb-1">No bank linked yet</p>
                    <p className="text-xs text-gray-500 max-w-xs mx-auto">
                      Link your bank to receive Quick Split payouts directly. Funds usually arrive within 1–3 business days.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {achMethods.map(pm => (
                      <div
                        key={pm.id}
                        className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors"
                        data-testid={`pm-ach-${pm.id}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center shrink-0">
                          <Building2 className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-gray-900 truncate">{pm.bank_name}</p>
                            {pm.id === defaultPm && (
                              <span className="text-[10px] uppercase tracking-wide text-green-700">· Default</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 tabular-nums mt-0.5">
                            ····{pm.last4}{pm.account_type ? ` · ${pm.account_type}` : ''}
                          </p>
                        </div>
                        <div className="flex items-center shrink-0">
                          {pm.id !== defaultPm && (
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-400 hover:text-amber-600" onClick={() => handleSetDefault(pm.id)} data-testid={`pm-default-${pm.id}`} aria-label="Make default">
                              <Star className="w-4 h-4" />
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-400 hover:text-red-600" onClick={() => handleRemove(pm.id)} data-testid={`pm-remove-${pm.id}`} aria-label="Remove">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Cards */}
            {cardMethods.length > 0 && (
              <Card data-testid="pm-card-section">
                <CardHeader>
                  <CardTitle className="text-base font-semibold flex items-center">
                    <CreditCard className="w-5 h-5 mr-2 text-blue-600" />
                    Cards
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {cardMethods.map(pm => (
                      <div key={pm.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg" data-testid={`pm-card-${pm.id}`}>
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 capitalize">{pm.brand}</p>
                            <p className="text-xs text-gray-400">····{pm.last4} · {pm.exp_month}/{pm.exp_year}</p>
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-600" onClick={() => handleRemove(pm.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentMethodsPage;
